import React, { useState } from 'react';
import { Modal, ModalBody, ModalHeader, Button } from 'reactstrap';
import { AlertMessage } from 'utilities/utils';
import { GruntWorxExportPickerProps } from './types';
import { validateDocuments, determineSourceType, getProgressMessage } from './utils';
import { useGruntWorxExport } from './useGruntWorxExport';
import ExportFailureView from './ExportFailureView';
import ExportSuccessView from './ExportSuccessView';
import ExportMainContent from './ExportMainContent';
import ExportProgressView from './ExportProgressView';

const GruntWorxExportPicker: React.FC<GruntWorxExportPickerProps> = ({ 
  selectedRecords, 
  cancelWidget 
}) => {
  const [showFailureDetails, setShowFailureDetails] = useState(false);
  const [showSuccessDetails, setShowSuccessDetails] = useState(false);

  const {
    isExporting,
    exportStatus,
    pollAttempts,
    connectionStatus,
    handleExport,
    resetExport,
  } = useGruntWorxExport((status) => {
    if (status.status === 'failed') {
      setShowFailureDetails(true);
    }

    if (status.status === 'completed' && status.success !== false) {
      setShowSuccessDetails(true);
    }
  });

  const { validRecords, invalidRecords } = validateDocuments(selectedRecords);

  const handleExportClick = async () => {
    if (connectionStatus !== 'Active') {
      AlertMessage('error', 'GruntWorx connection is not active. Please check your integration settings.', 4000);
      return;
    }

    const documentIds = validRecords.map(record => record.uuid);
    
    if (documentIds.length === 0) {
      AlertMessage('error', 'No valid files found for export. Please ensure files have a Client ID and are in supported formats (.pdf, .zip, .jpg, .jpeg, .png).', 4000);
      return;
    }

    setShowFailureDetails(false);
    const sourceType = determineSourceType(validRecords);
    
    try {
      await handleExport(documentIds, sourceType);
    } catch (error) {
      setShowFailureDetails(true);
    }
  };

  const handleRetry = () => {
    setShowFailureDetails(false);
    resetExport();
    handleExportClick();
  };

  const handleClose = () => {
    setShowFailureDetails(false);
    resetExport();
    cancelWidget();
  };

  const handleSuccessClose = () => {
    resetExport();
    cancelWidget();
  };

  const renderModalContent = () => {
    if (showSuccessDetails) {
      return (
        <ExportSuccessView
          exportStatus={exportStatus!}
          onClose={handleSuccessClose}
        />
      );
    }
    
    if (showFailureDetails) {
      return (
        <ExportFailureView
          exportStatus={exportStatus!}
          onRetry={handleRetry}
          onClose={handleClose}
        />
      );
    }
    
    if (isExporting) {
      return (
        <ExportProgressView
          exportStatus={exportStatus}
          pollAttempts={pollAttempts}
          progressMessage={getProgressMessage(isExporting, exportStatus)}
        />
      );
    }
    
    return (
      <ExportMainContent
        connectionStatus={connectionStatus}
        validRecords={validRecords}
        invalidRecords={invalidRecords}
      />
    );
  };

  return (
    <Modal
      isOpen
      className="customModal customModal--center customModal--alert customModal--w600 customModal--nopadding"
    >
      <ModalHeader className="ModalHeader">
        <button
          type="button"
          onClick={cancelWidget}
          className="close"
          aria-label="Close"
          disabled={isExporting}
        >
          <i className="icon-close2" />
        </button>
      </ModalHeader>
      <ModalHeader className="modalHeader">
        <div className="text-medium pl-2">
          <div>Export to GruntWorx</div>
        </div>
      </ModalHeader>
      <ModalBody className="modalContent">
        <div className="modalContent--inner">
          {renderModalContent()}
          
          {!showFailureDetails && !showSuccessDetails && (
            <div className="row">
              <div className="col-12 justify-content-end formbtn">
                <div className="btn-wrap btn--leftspace">
                  <Button
                    className="btn btn-outline-light"
                    onClick={cancelWidget}
                    disabled={isExporting}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="btn btn-primary"
                    onClick={handleExportClick}
                    disabled={isExporting || connectionStatus !== 'Active' || validRecords.length === 0}
                  >
                    Export
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default GruntWorxExportPicker; 